import React, { useState, useEffect } from 'react';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import styles from './UpdatesTabContent.module.css';
import CreateButton from './CreateButton';
import { fetchUnitUpdates, deleteUnitUpdate } from '../services/unitService';
import UnitUpdateDetails from './UnitUpdateDetails';
import AddUnitUpdateForm from './AddUnitUpdateForm';

export default function UnitUpdates({ unit, onBack }) {
    // Get unit update permissions from cached permissions
    const {
        hasAddUnitUpdates,
        hasDeleteUnitUpdates,
        hasUpdateUnitUpdates
    } = useCachedPermissions();

    const [updates, setUpdates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 8, results: 0, last_page: 1 });
    const [showDetails, setShowDetails] = useState(false);
    const [selectedUpdateId, setSelectedUpdateId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);
        fetchUnitUpdates(unit.id, page, pagination.limit)
            .then(res => {
                if (isMounted) {
                    console.log('Full response from fetchUnitUpdates:', res);
                    setUpdates(res.data.data || []);
                    setPagination({
                        total: res.data.total,
                        page: res.data.page,
                        limit: res.data.limit,
                        results: res.data.results,
                        last_page: res.data.last_page
                    });
                    setTotalPages(res.data.last_page || 1);
                    setLoading(false);
                }
            })
            .catch(err => {
                if (isMounted) {
                    setError(err.message);
                    setLoading(false);
                }
            });
        return () => { isMounted = false; };
    }, [unit.id, page]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const handleAddSuccess = (newUpdate) => {
        setShowAdd(false);
        setPage(1);
        if (newUpdate) {
            setUpdates(prev => [newUpdate, ...prev]);
        }
    };

    const handleDeleteUpdate = async (updateId) => {
        if (!window.confirm('Are you sure you want to delete this update?')) return;
        setDeleteLoading(true);
        setDeleteError('');
        try {
            await deleteUnitUpdate(updateId);
            setUpdates(prev => prev.filter(u => u.id !== updateId && u._id !== updateId));
            setShowDetails(false);
        } catch (err) {
            setDeleteError(err.message || 'Failed to delete update');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleEditUpdate = (updated) => {
        setUpdates(prev => prev.map(u => {
            const uid = u.id || u._id;
            const updatedId = updated.id || updated._id;
            return uid === updatedId ? updated : u;
        }));
        setShowDetails(false);
    };

    if (showDetails && selectedUpdateId) {
        return <UnitUpdateDetails updateId={selectedUpdateId} onBack={() => setShowDetails(false)} onDelete={handleDeleteUpdate} onEdit={handleEditUpdate} loading={deleteLoading} error={deleteError} />;
    }

    if (showAdd) {
        return <AddUnitUpdateForm unitId={unit.id} onCancel={() => setShowAdd(false)} onSuccess={handleAddSuccess} />;
    }

    return (
        <div className={styles.updatesContainer}>
            <div className={styles.headerContainer}>
                <div className={styles.headerLeft}>
                    <button className={styles.backButton} onClick={onBack}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5" />
                            <path d="M12 19l-7-7 7-7" />
                        </svg>
                        Back
                    </button>
                    <h2 className={styles.headerTitle}>Updates for {unit.name || unit.label}</h2>
                </div>
                {hasAddUnitUpdates && (
                    <CreateButton label="+ Add Update" onClick={() => setShowAdd(true)} />
                )}
            </div>
            {loading ? (
                <div className={styles.noDataMessage}><p>Loading...</p></div>
            ) : error ? (
                <div className={styles.noDataMessage}><p>{error}</p></div>
            ) : updates.length === 0 ? (
                <div className={styles.noDataMessage}><p>No updates found for this unit.</p></div>
            ) : (
                <>
                    <div className={styles.unitsGrid}>
                        {updates.map(update => (
                            <div key={update.id || update._id} className={styles.unitCard}>
                                <div className={styles.unitCardImg} onClick={() => { setSelectedUpdateId(update.id || update._id); setShowDetails(true); }}>
                                    {update.images && update.images.length > 0 ? (
                                        <div className={styles.imageItem}>
                                            <img src={update.images[0]} alt={update.unit_name} />
                                            <button
                                                className={styles.deleteIcon}
                                                title="Delete update"
                                                onClick={e => { e.stopPropagation(); handleDeleteUpdate(update.id || update._id); }}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3,6 5,6 21,6" />
                                                    <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                                                    <line x1="10" y1="11" x2="10" y2="17" />
                                                    <line x1="14" y1="11" x2="14" y2="17" />
                                                </svg>
                                            </button>
                                        </div>
                                    ) : (
                                        <div className={styles.imageItem}>
                                            <div style={{ width: '100%', height: '200px', background: '#222', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>No Image</div>
                                            <button
                                                className={styles.deleteIcon}
                                                title="Delete update"
                                                onClick={e => { e.stopPropagation(); handleDeleteUpdate(update.id || update._id); }}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3,6 5,6 21,6" />
                                                    <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                                                    <line x1="10" y1="11" x2="10" y2="17" />
                                                    <line x1="14" y1="11" x2="14" y2="17" />
                                                </svg>
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className={styles.unitCardContent}>
                                    <h3 className={styles.unitCardTitle}>{update.unit_name}</h3>
                                    <p className={styles.unitCardDesc}>{update.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
                            <button onClick={() => handlePageChange(page - 1)} disabled={page === 1} className={styles.actionButton}>&lt; Prev</button>
                            <span style={{ color: '#fff', margin: '0 16px', alignSelf: 'center' }}>Page {page} of {totalPages}</span>
                            <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages} className={styles.actionButton}>Next &gt;</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
} 